import { RotateCcw } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { Button, Card, CardContent } from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';

/**
 * Catches a render error in the routed page and shows it, instead of losing the app.
 *
 * Without this, one throwing element unmounts the entire tree and the browser shows a
 * blank white page: no sidebar, no message, no clue which page it was. That is what a
 * bare `<SelectLabel>` outside `<SelectGroup>` did — Radix builds that context with no
 * default, so the hook throws, and a component throwing is indistinguishable from the
 * app not existing.
 *
 * Deliberately placed INSIDE the shell, around the outlet only. The sidebar and top bar
 * keep rendering, so the error is one broken page rather than one broken product, and
 * navigating elsewhere is still possible without a reload.
 *
 * A class is the whole implementation: `componentDidCatch` has no hook equivalent, and
 * `react-error-boundary` would be a dependency for thirty lines.
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // The stack is the only way back to the real cause, and the fallback below shows
    // just the message. Keep it in the console rather than swallowing it.
    console.error('Page crashed:', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <PageContainer>
        <Card>
          <CardContent className="space-y-4 py-14 text-center">
            <div>
              <p className="text-sm font-medium">This page hit an error</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                Nothing was saved. The rest of the app still works — pick another page from the
                sidebar, or reload to try again.
              </p>
            </div>
            {/* Shown, not hidden: this is an internal tool, and the message is what makes
                a bug report actionable instead of "it went white". */}
            <pre className="mx-auto max-w-lg overflow-x-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
              {error.message}
            </pre>
            <Button hint={"Reload the page"} variant="outline" className="gap-2" onClick={() => window.location.reload()}>
              <RotateCcw className="size-4" />
              Reload
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }
}

/**
 * Keyed on the path, which is what makes the boundary recoverable.
 *
 * An error boundary never clears itself. Without the key, the first crash would pin the
 * fallback in place for the rest of the session and every later page would look broken
 * too; remounting on navigation resets it for free.
 */
export function PageErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}
